/**
 * Hyperliquid health state machine (#771). Pure: no timers, no I/O, time is passed in on every event.
 *
 * One machine per network (testnet / mainnet). The runtime (healthMonitor.ts) feeds it what the app already sees:
 * every /info attempt (ok + latency / error / timeout), /exchange transport failures (never order rejections),
 * WebSocket up / down / idle transitions, "socket open but silent" staleness, and a 1 s `tick` so time-based rules
 * fire without traffic.
 *
 * The rules and thresholds are shared with Android/iOS through shared/fixtures/health.json (schema
 * hip4.conformance.health/1). health.test.ts replays every case of that file against this code; native apps must do
 * the same. Change the rules here AND in the fixture together.
 *
 * Rules (rank ok < degraded < down):
 *   failure  = info_error | info_timeout | exchange_error   (consecutive count: failStreak, first at failSince)
 *   slow     = info_ok with latencyMs > slowMs, or info_slow (HTTP 429)   (consecutive count: slowStreak)
 *   info_ok (fast) resets both streaks; a failure resets slowStreak; a slow response resets failStreak.
 *   wsDownFor = t - wsDownSince while the socket is reconnecting (ws_down .. ws_up / ws_idle), else 0.
 *
 *   raw = down     if failStreak >= downFailures AND t - failSince >= downSpanMs
 *                  or wsDownFor >= wsDownMs AND failStreak >= wsDownMinFailures
 *   raw = degraded if failStreak >= degradedFailures, slowStreak >= degradedSlow, wsDownFor >= wsDegradedMs, or wsStale
 *   raw = ok       otherwise
 *
 *   Getting worse is immediate (the N / M thresholds above are the hysteresis on the way in).
 *   Getting better needs recoverMs with no bad signal (failure, slow, ws_stale) while raw stays better than the
 *   current status; then status = raw. Any bad signal restarts that clock.
 */

export type HealthStatus = 'ok' | 'degraded' | 'down';

export interface HealthThresholds {
  /** An /info attempt answered slower than this counts as slow. */
  slowMs: number;
  /** An /info attempt with no answer after this long counts as a failure (info_timeout). */
  infoTimeoutMs: number;
  /** Consecutive failures that make the network degraded. */
  degradedFailures: number;
  /** Consecutive slow answers that make the network degraded. */
  degradedSlow: number;
  /** Consecutive failures that, spanning at least downSpanMs, make the network down. */
  downFailures: number;
  downSpanMs: number;
  /** WebSocket reconnecting at least this long: degraded. */
  wsDegradedMs: number;
  /** WebSocket reconnecting at least this long AND failStreak >= wsDownMinFailures: down. */
  wsDownMs: number;
  wsDownMinFailures: number;
  /** Socket open with live subscriptions but no frame at all (data or pong) for this long: stale (degraded). */
  wsStaleMs: number;
  /** Time with no bad signal before the status may improve. */
  recoverMs: number;
  /** Runtime only (not machine rules): probe /info when no /info answer arrived for this long while ok. */
  probeIdleMs: number;
  /** Runtime only: probe interval while degraded / down. */
  probeUnhealthyMs: number;
}

export const DEFAULT_HEALTH_THRESHOLDS: Readonly<HealthThresholds> = Object.freeze({
  slowMs: 3_000,
  infoTimeoutMs: 10_000,
  degradedFailures: 3,
  degradedSlow: 3,
  downFailures: 5,
  downSpanMs: 20_000,
  wsDegradedMs: 15_000,
  wsDownMs: 30_000,
  wsDownMinFailures: 2,
  wsStaleMs: 45_000,
  recoverMs: 20_000,
  probeIdleMs: 45_000,
  probeUnhealthyMs: 10_000,
});

export type HealthEvent =
  | { type: 'info_ok'; t: number; latencyMs: number }
  | { type: 'info_slow'; t: number }
  | { type: 'info_error'; t: number }
  | { type: 'info_timeout'; t: number }
  | { type: 'exchange_ok'; t: number }
  | { type: 'exchange_error'; t: number }
  | { type: 'ws_up'; t: number }
  | { type: 'ws_down'; t: number }
  | { type: 'ws_idle'; t: number }
  | { type: 'ws_stale'; t: number }
  | { type: 'ws_fresh'; t: number }
  | { type: 'tick'; t: number };

export type HealthEventType = HealthEvent['type'];

export interface HealthState {
  status: HealthStatus;
  failStreak: number;
  failSince: number | null;
  slowStreak: number;
  wsDownSince: number | null;
  wsStale: boolean;
  /** Start of the current "raw better than status, no bad signal" window. */
  betterSince: number | null;
  /** When status last changed (null: never). */
  since: number | null;
}

export const INITIAL_HEALTH: Readonly<HealthState> = Object.freeze({
  status: 'ok',
  failStreak: 0,
  failSince: null,
  slowStreak: 0,
  wsDownSince: null,
  wsStale: false,
  betterSince: null,
  since: null,
});

const RANK: Record<HealthStatus, number> = { ok: 0, degraded: 1, down: 2 };

export function rawStatus(s: HealthState, t: number, th: HealthThresholds): HealthStatus {
  const wsDownFor = s.wsDownSince === null ? 0 : t - s.wsDownSince;
  const failSpan = s.failSince === null ? 0 : t - s.failSince;
  if (s.failStreak >= th.downFailures && failSpan >= th.downSpanMs) return 'down';
  if (wsDownFor >= th.wsDownMs && s.failStreak >= th.wsDownMinFailures) return 'down';
  if (
    s.failStreak >= th.degradedFailures ||
    s.slowStreak >= th.degradedSlow ||
    wsDownFor >= th.wsDegradedMs ||
    s.wsStale
  )
    return 'degraded';
  return 'ok';
}

/** Apply one event. Returns a new state object (the input is not mutated). */
export function reduceHealth(
  prev: HealthState,
  ev: HealthEvent,
  th: HealthThresholds = DEFAULT_HEALTH_THRESHOLDS,
): HealthState {
  const s: HealthState = { ...prev };
  const t = ev.t;
  let bad = false;
  const fail = () => {
    if (s.failStreak === 0) s.failSince = t;
    s.failStreak++;
    s.slowStreak = 0;
    bad = true;
  };
  const okAnswer = () => {
    s.failStreak = 0;
    s.failSince = null;
  };
  switch (ev.type) {
    case 'info_ok':
      okAnswer();
      if (ev.latencyMs > th.slowMs) {
        s.slowStreak++;
        bad = true;
      } else s.slowStreak = 0;
      break;
    case 'info_slow':
      okAnswer();
      s.slowStreak++;
      bad = true;
      break;
    case 'info_error':
    case 'info_timeout':
    case 'exchange_error':
      fail();
      break;
    case 'exchange_ok':
      okAnswer();
      break;
    case 'ws_up':
    case 'ws_idle':
      s.wsDownSince = null;
      s.wsStale = false;
      break;
    case 'ws_down':
      if (s.wsDownSince === null) s.wsDownSince = t;
      s.wsStale = false;
      break;
    case 'ws_stale':
      s.wsStale = true;
      bad = true;
      break;
    case 'ws_fresh':
      s.wsStale = false;
      break;
    case 'tick':
      break;
  }

  const raw = rawStatus(s, t, th);
  const setStatus = (next: HealthStatus) => {
    if (next !== s.status) s.since = t;
    s.status = next;
    s.betterSince = null;
  };
  if (RANK[raw] > RANK[s.status]) setStatus(raw);
  else if (RANK[raw] < RANK[s.status]) {
    if (bad || s.betterSince === null) s.betterSince = t;
    if (t - s.betterSince >= th.recoverMs) setStatus(raw);
  } else s.betterSince = null;
  return s;
}

/** Copy for the banner / ticket. `network` is the env name ("testnet" | "mainnet"). */
export function healthMessage(status: HealthStatus, network: string): string | null {
  if (status === 'degraded')
    return `Hyperliquid ${network} is slow or partly unavailable. Prices may be stale and orders may fail.`;
  if (status === 'down') return `Hyperliquid ${network} isn't responding. Orders are paused until it's back.`;
  return null;
}
